/**
 * BatchUnifiedConfig - Unified mode configuration section
 *
 * Handles template/collection/prompt mode configuration for batch generation.
 */

import { BatchModeSelector } from './BatchModeSelector';
import { BatchVariantPicker } from './BatchVariantPicker';
import { BatchCostPreview } from './BatchCostPreview';
import {
  TemplateSelector,
  CollectionSelector,
  PromptInput,
  AspectRatioSelector,
} from './BatchInputSelectors';
import type { BatchMode } from '@/lib/api/batch';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
  max_images: number;
}

interface BatchUnifiedConfigProps {
  mode: BatchMode;
  onModeChange: (mode: BatchMode) => void;
  templateId: string;
  onTemplateChange: (id: string) => void;
  collectionId: string;
  onCollectionChange: (id: string) => void;
  customPrompt: string;
  onCustomPromptChange: (prompt: string) => void;
  variants: number;
  onVariantsChange: (v: number) => void;
  aspectRatio: string;
  onAspectRatioChange: (ratio: string) => void;
  templates: Template[];
  collections: Collection[];
  collectionImageCount: number;
  inputCount: number;
  totalGenerations: number;
  costPerImage: number;
  totalCost: number;
  discountPercent: number;
  disabled?: boolean;
}

export function BatchUnifiedConfig({
  mode,
  onModeChange,
  templateId,
  onTemplateChange,
  collectionId,
  onCollectionChange,
  customPrompt,
  onCustomPromptChange,
  variants,
  onVariantsChange,
  aspectRatio,
  onAspectRatioChange,
  templates,
  collections,
  collectionImageCount,
  inputCount,
  totalGenerations,
  costPerImage,
  totalCost,
  discountPercent,
  disabled = false,
}: BatchUnifiedConfigProps) {
  return (
    <div className="space-y-6">
      <BatchModeSelector mode={mode} onModeChange={onModeChange} disabled={disabled} />

      <div className="space-y-4">
        {mode === 'template' && (
          <TemplateSelector
            templateId={templateId}
            templates={templates}
            onChange={onTemplateChange}
            disabled={disabled}
          />
        )}
        {mode === 'collection' && (
          <CollectionSelector
            collectionId={collectionId}
            collections={collections}
            collectionImageCount={collectionImageCount}
            onChange={onCollectionChange}
            disabled={disabled}
          />
        )}
        {mode === 'prompt' && (
          <PromptInput value={customPrompt} onChange={onCustomPromptChange} disabled={disabled} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {mode !== 'collection' && (
          <BatchVariantPicker value={variants} onChange={onVariantsChange} max={5} disabled={disabled} />
        )}
        <AspectRatioSelector value={aspectRatio} onChange={onAspectRatioChange} disabled={disabled} />
      </div>

      <BatchCostPreview
        inputImages={inputCount}
        variantsPerImage={mode === 'collection' ? collectionImageCount : variants}
        totalGenerations={totalGenerations}
        costPerImage={costPerImage}
        totalCost={totalCost}
        discountPercent={discountPercent}
      />
    </div>
  );
}
